import React, { createContext, useContext, useMemo, useState } from 'react';
import { PLACEMENT, SnackbarProvider } from 'baseui/snackbar';
import { Tabs } from './const';
import type { ModernizedGenericAnnotation, SidePanelContextProps, SidePanelProviderProps, TabsType } from './types';
type SidePanelContext = SidePanelContextProps<ModernizedGenericAnnotation> & {
  activeTab: TabsType;
  setActiveTab: React.Dispatch<React.SetStateAction<TabsType>>;
};
const SidePanelContext = createContext<SidePanelContext | null>(null);
export const SidePanelProvider = <Annotation extends ModernizedGenericAnnotation,>({
  children,
  onTabChange,
  defaultTab,
  taskQuestionnaireComponent,
  relationshipComponent,
  annotationFilterComponent,
  createRelationshipComponent,
  annotationLabelsAtom,
  annotationsAtom,
  dispatchAtom,
  toolConfig,
  selectedAnnotationLabelAtom,
  selectedAnnotationIdsAtom,
  focusedAnnotationIdAtom,
  showAnnotationDetailsAtom,
  filteredAnnotationsAtom,
  editAnnotationComponent,
  labelListItemEnhancer,
  pendingAnnotationAtom,
  deleteAnnotationModalContentComponent,
  mlAnnotationsAtom,
  importedMLAnnotationIdsAtom,
  numAnnotationFramesAtom,
  selectedFilterLabelAtom,
  additionalOptionsComponent,
  expandedAnnotationIdsAtom,
  expandedAnnotationSectionIdsAtom,
  visibilityModeAtom
}: SidePanelProviderProps<Annotation>) => {
  const [activeTab, setActiveTab] = useState<TabsType>(defaultTab || Tabs.LABELS.key);
  const value = useMemo(() => ({
    onTabChange,
    defaultTab,
    taskQuestionnaireComponent,
    relationshipComponent,
    annotationFilterComponent,
    createRelationshipComponent,
    annotationLabelsAtom,
    filteredAnnotationsAtom,
    annotationsAtom,
    mlAnnotationsAtom,
    importedMLAnnotationIdsAtom,
    dispatchAtom,
    selectedAnnotationLabelAtom,
    toolConfig,
    activeTab,
    setActiveTab,
    selectedAnnotationIdsAtom,
    expandedAnnotationSectionIdsAtom,
    selectedFilterLabelAtom,
    focusedAnnotationIdAtom,
    showAnnotationDetailsAtom,
    editAnnotationComponent,
    labelListItemEnhancer,
    pendingAnnotationAtom,
    deleteAnnotationModalContentComponent,
    numAnnotationFramesAtom,
    expandedAnnotationIdsAtom,
    additionalOptionsComponent,
    visibilityModeAtom
  }), [onTabChange, defaultTab, taskQuestionnaireComponent, relationshipComponent, annotationFilterComponent, createRelationshipComponent, annotationLabelsAtom, filteredAnnotationsAtom, annotationsAtom, mlAnnotationsAtom, importedMLAnnotationIdsAtom, dispatchAtom, selectedAnnotationLabelAtom, toolConfig, activeTab, selectedAnnotationIdsAtom, expandedAnnotationSectionIdsAtom, selectedFilterLabelAtom, focusedAnnotationIdAtom, showAnnotationDetailsAtom, editAnnotationComponent, labelListItemEnhancer, pendingAnnotationAtom, deleteAnnotationModalContentComponent, numAnnotationFramesAtom, expandedAnnotationIdsAtom, additionalOptionsComponent, visibilityModeAtom]);
  return <SidePanelContext.Provider value={value}>
      <SnackbarProvider placement={PLACEMENT.topRight}>{children}</SnackbarProvider>
    </SidePanelContext.Provider>;
};
export const useSidePanel = (): SidePanelContext => {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error('useSidePanel must be used within a SidePanelProvider');
  }
  return context;
};